import { configuration } from 'src/configuration';
import {
  OrganizationDatasourcesQueryDto,
  OrganizationResearcher,
  OrganizationResearcherRole,
  OrganizationResearchersQueryDto,
} from 'src/types/organizations';

const { accountApiUrl } = configuration;

interface OrganizationToDataSourceDTO {
  token: string;
  orgId: string;
  dataSourceId: string;
}

export type ConfigPayload = Record<string, string>;

export const getOrganizationById = async function ({ token, id }: { token: string; id: string }) {
  const response = await fetch(`${accountApiUrl}/organizations/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  return data;
};

export const getOrganizationGeneralInfo = async function ({
  token,
  id,
}: {
  token: string;
  id: string;
}) {
  const response = await fetch(`${accountApiUrl}/organizations/${id}/general-info`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();
  return data;
};

export const getOrganizations = async function ({
  token,
  search,
  after,
}: {
  search: string;
  token: string;
  after: string;
}) {
  const response = await fetch(
    `${accountApiUrl}/organizations?search=${search}&limit=15&after=${after}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  if (data && data.organizations) {
    return {
      organizations: data.organizations,
      lastEvaluatedKey: data.lastEvaluatedKey,
    };
  }

  return { organizations: [], lastEvaluatedKey: null };
};

export const getOrganizationsByResearcherId = async function ({
  token,
  researcherId,
}: {
  token: string;
  researcherId: string;
}) {
  const response = await fetch(`${accountApiUrl}/researchers/${researcherId}/organizations`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  return {
    organizations: data.organizations.map((organization: OrganizationResearcher) => ({
      ...organization,
      name: organization.organizationName,
      displayName: organization.organizationDisplayName,
    })),
  };
};

export interface CreateOrganizationDTO {
  name: string;
  displayName: string;
}

export const createOrganization = async function (
  token: string,
  createOrganizationDto: CreateOrganizationDTO,
) {
  const response = await fetch(`${accountApiUrl}/organizations`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify({ ...createOrganizationDto }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  return data.organization;
};

export interface UpdateOrganizationDTO {
  organizationId: string;
  name: string;
  displayName: string;
  orgWideAccess: boolean;
}

export const updateOrganization = async function ({
  token,
  updatedOrganization,
}: {
  token: string;
  updatedOrganization: UpdateOrganizationDTO;
}) {
  const response = await fetch(
    `${accountApiUrl}/organizations/${updatedOrganization.organizationId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      method: 'PATCH',
      body: JSON.stringify({ ...updatedOrganization }),
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  return data.user;
};

export const deleteOrganization = async function ({ id, token }: { id: string; token: string }) {
  const response = await fetch(`${accountApiUrl}/organizations/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }
};
export const getConfigurationByOrganizationId = async ({
  token,
  organizationId,
}: {
  token: string;
  organizationId: string;
}): Promise<string> => {
  const response = await fetch(`${accountApiUrl}/organizations/${organizationId}/config`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();
  return JSON.stringify(data, null, 2);
};

export const updateConfigurationByOrganizatioId = async ({
  token,
  payload,
  organizationId,
}: {
  token: string;
  payload: ConfigPayload;
  organizationId: string;
}): Promise<void> => {
  try {
    const response = await fetch(`${accountApiUrl}/organizations/${organizationId}/config`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      method: 'PUT',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }

    await response.json();
    return;
  } catch (error) {
    return;
  }
};

export const inviteResearcherToOrganization = async ({
  token,
  organizationId,
  email,
  role,
}: {
  token: string;
  organizationId: string;
  email: string;
  role: OrganizationResearcherRole;
}): Promise<void> => {
  const response = await fetch(
    `${accountApiUrl}/organizations/${organizationId}/invite-researcher`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({ email, role }),
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  return;
};

export const getOrganizationResearchers = async ({
  token,
  organizationId,
}: {
  token: string;
  organizationId: string;
}): Promise<OrganizationResearchersQueryDto> => {
  const response = await fetch(`${accountApiUrl}/organizations/${organizationId}/researchers`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();
  return data;
};

export const getOrganizationDatasources = async ({
  token,
  organizationId,
}: {
  token: string;
  organizationId: string;
}): Promise<OrganizationDatasourcesQueryDto> => {
  const response = await fetch(`${accountApiUrl}/organizations/${organizationId}/data-sources`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();
  return data;
};

export const addOrganizationToDataSource = async ({
  token,
  orgId,
  dataSourceId,
}: OrganizationToDataSourceDTO) => {
  const response = await fetch(
    `${accountApiUrl}/data-sources/${dataSourceId}/organization/${orgId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  return 'Success';
};

export const removeOrganizationFromDataSource = async ({
  token,
  orgId,
  dataSourceId,
}: OrganizationToDataSourceDTO) => {
  const response = await fetch(
    `${accountApiUrl}/data-sources/${dataSourceId}/organization/${orgId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      method: 'DELETE',
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }
};

export const getOrganizationStudies = async ({
  token,
  organizationId,
}: {
  token: string;
  organizationId: string;
}) => {
  const response = await fetch(`${accountApiUrl}/organizations/${organizationId}/studies`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  return data;
};
